import {
  ACT_SECTION_FIELDS,
  REQUIRED_ACT_SECTIONS,
  REQUIRED_SAT_SECTIONS,
  SAT_SECTION_FIELDS,
  SectionField,
  TARGET_SCORE_RANGE,
  testsInSelection,
  ZIP_PATTERN,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  ActScores,
  BoosterDraft,
  BoosterTargets,
  SatScores,
  StepKey,
  WhichTests,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { isStartDateInRange, isTestDateInRange } from './coverageEstimate';

export type { StepKey };

/**
 * Step sequence varies by test choice: ACT and SAT each get a score screen, so "Both" spans two.
 * There is no report step — leaving the last score screen queues the report into the chat and
 * advances straight to Targets.
 */
export const buildSteps = (whichTests: WhichTests | null): StepKey[] => {
  const steps: StepKey[] = ['INTAKE'];
  const keeps = testsInSelection(whichTests);

  if (keeps.act) steps.push('SCORE_ACT');
  if (keeps.sat) steps.push('SCORE_SAT');

  steps.push('TARGETS', 'SCHEDULE');

  return steps;
};

export const getNextStep = (steps: StepKey[], current: StepKey): StepKey | undefined =>
  steps[steps.indexOf(current) + 1];

export const getPreviousStep = (steps: StepKey[], current: StepKey): StepKey | undefined => {
  const index = steps.indexOf(current);

  return index > 0 ? steps[index - 1] : undefined;
};

const hasValidSectionScores = (
  scores: Readonly<Record<string, number | null>>,
  fields: readonly SectionField[],
  required: readonly string[],
): boolean =>
  fields.every(({ key, min, max }) => {
    const value = scores[key];

    return value === null ? !required.includes(key) : value >= min && value <= max;
  });

/** Whether the entered SAT total agrees with the section sum; true while any of the three is blank. */
export const satTotalMatchesSections = (scores: SatScores): boolean =>
  scores.total === null || scores.erw === null || scores.math === null
    ? true
    : scores.total === scores.erw + scores.math;

const roundHalfUp = (value: number): number => Math.floor(value + 0.5);

/**
 * Whether the entered ACT composite disagrees with the section scores. The
 * composite is normally the rounded mean of the sections, so it should match
 * either the mean of English/Math/Reading (current scoring) or the mean of all
 * four including Science (older reports). Returns false until the composite and
 * the three core sections are all entered. This never gates advancing — a
 * superscore can legitimately match neither — it only drives a soft warning.
 */
export const actCompositeMismatch = (scores: ActScores): boolean => {
  const { composite, english, math, reading, science } = scores;

  if (composite === null || english === null || math === null || reading === null) return false;

  const candidates = [roundHalfUp((english + math + reading) / 3)];

  if (science !== null) candidates.push(roundHalfUp((english + math + reading + science) / 4));

  return !candidates.includes(composite);
};

export type TargetsErrors = Readonly<{
  targetScore: boolean;
  testDate: boolean;
  startDate: boolean;
  studyDays: boolean;
}>;

/**
 * Which Targets fields fail the plan's requirements, mirroring the save-time checks. A null start
 * date reads as today, which the window always admits, so only a picked date can go stale.
 */
export const getTargetsErrors = (targets: BoosterTargets): TargetsErrors => {
  const range = targets.targetTest ? TARGET_SCORE_RANGE[targets.targetTest] : null;
  const { testDate } = targets;

  return {
    targetScore:
      targets.targetScore === null ||
      (range !== null && (targets.targetScore < range.min || targets.targetScore > range.max)),
    testDate: !testDate || !isTestDateInRange(testDate),
    startDate: targets.startDate !== null && !isStartDateInRange(targets.startDate, testDate),
    studyDays: targets.studyDays.length === 0,
  };
};

export type IntakeRequirements = Readonly<{
  firstName: boolean;
  zip: boolean;
  school: boolean;
  expectedGraduationYear: boolean;
  programStart: boolean;
}>;

/**
 * Which intake fields the form asks for. A field that is required to advance but never asked
 * would disable Next with nothing on screen to fix, so requiring a field means reading it here.
 * Zip, school, graduation year and program start are asked of guests only; school is asked but
 * never gated.
 */
export const intakeRequirements = ({
  loggedIn,
  userFirstName,
}: {
  loggedIn: boolean;
  userFirstName?: string;
}): IntakeRequirements => ({
  firstName: !loggedIn || !userFirstName?.trim(),
  zip: !loggedIn,
  school: !loggedIn,
  expectedGraduationYear: !loggedIn,
  programStart: !loggedIn,
});

/** Required-field and score-range gate between steps. */
export const canAdvance = (step: StepKey, draft: BoosterDraft, loggedIn: boolean): boolean => {
  switch (step) {
    case 'INTAKE': {
      const asks = intakeRequirements({ loggedIn });

      return (
        // Required even when unasked: an authed student's name is prefilled from their record,
        // and the submission has no other source for it.
        draft.firstName.trim().length > 0 &&
        (!asks.expectedGraduationYear || draft.expectedGraduationYear !== null) &&
        (!asks.programStart || draft.programStartYear !== null) &&
        (!asks.zip || ZIP_PATTERN.test(draft.zip)) &&
        draft.whichTests !== null &&
        // "Haven't taken yet" is the no-score path — it cannot advance.
        draft.whichTests !== 'none'
      );
    }
    case 'SCORE_ACT':
      return hasValidSectionScores(draft.actScores, ACT_SECTION_FIELDS, REQUIRED_ACT_SECTIONS);
    case 'SCORE_SAT':
      return (
        hasValidSectionScores(draft.satScores, SAT_SECTION_FIELDS, REQUIRED_SAT_SECTIONS) &&
        satTotalMatchesSections(draft.satScores)
      );
    case 'TARGETS':
      return !getTargetsErrors(draft.targets).targetScore;
    case 'SCHEDULE': {
      const errors = getTargetsErrors(draft.targets);

      return !errors.testDate && !errors.startDate && !errors.studyDays;
    }
    default:
      return false;
  }
};
